/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects. Never import the generated SDK directly from feature code —
 * import these hooks (or the SDK re-exports from `@/lib/api`) instead.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addProjectMember,
  adminActivityActions,
  adminActivityAnalytics,
  adminArchiveProject,
  adminBackfillRepoSetup,
  adminDeleteProject,
  adminDeleteUser,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListUsers,
  adminPromoteProjectToProd,
  adminRefreshProjectLastLogins,
  adminRefreshProjects,
  adminResetUserPassword,
  adminResyncProjectRailway,
  adminRevokeUserSessions,
  adminSetProjectClient,
  adminUpdateUser,
  changePassword,
  createClient,
  deleteClient,
  forgotPassword,
  getAccessStatus,
  getActivity,
  getClient,
  getHealth,
  getMe,
  getMeDetail,
  getProjectResourcesStatus,
  getVersion,
  githubConnect,
  githubDisconnect,
  githubStatus,
  inviteUser,
  listClients,
  listProjectMembers,
  listProjects,
  login,
  logout,
  removeProjectMember,
  researchClientBrand,
  resetPassword,
  resetProjectDbPassword,
  searchUsers,
  signup,
  updateClient,
  type UpdateClientRequest,
  updateProfile,
} from "@/lib/api/generated";
import type { ResourceStatusMap } from "@/lib/local-types";

// ─── Query keys ────────────────────────────────────────────────────────────

/** Filter/sort/pagination params for the admin activity feed. */
export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

/** Server-side sort selection for the users/projects list views. */
export interface TableSortParams {
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export const queryKeys = {
  health: ["health"] as const,
  version: ["version"] as const,
  me: ["me"] as const,
  meDetail: ["me", "detail"] as const,
  activity: ["activity"] as const,
  projects: ["projects"] as const,
  clients: ["clients"] as const,
  client: (clientId: number | null) => ["clients", clientId] as const,
  projectMembers: (projectId: number) => ["projects", projectId, "members"] as const,
  projectResourceStatus: (projectId: number) => ["projects", projectId, "resources"] as const,
  githubStatus: ["github", "status"] as const,
  accessStatus: ["access", "status"] as const,
  adminUsers: ["admin", "users"] as const,
  adminUser: (userId: string | null) => ["admin", "users", userId] as const,
  adminActivity: (params: AdminActivityParams) => ["admin", "activity", params] as const,
  adminActivityAnalytics: (days: number) => ["admin", "activity", "analytics", days] as const,
  adminActivityActions: ["admin", "activity", "actions"] as const,
  userSearch: (query: string, exclude: string) => ["users", "search", query, exclude] as const,
};

// ─── System ──────────────────────────────────────────────────────────────

export function useHealth(enabled = true, refetchInterval?: number) {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: async () => {
      const { data } = await getHealth();
      return data ?? null;
    },
    enabled,
    refetchInterval,
  });
}

export function useVersion(enabled = true) {
  return useQuery({
    queryKey: queryKeys.version,
    queryFn: async () => {
      const { data } = await getVersion();
      return data ?? null;
    },
    enabled,
  });
}

// ─── Profile / auth ──────────────────────────────────────────────────────

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data } = await getMe();
      return data ?? null;
    },
    // Don't retry on a 401 — ProtectedRoute redirects to /login.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string }) => {
      const { data } = await login({ body: { email: vars.email, password: vars.password } });
      return data ?? null;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data — and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data } = await signup({
        body: {
          email: vars.email,
          password: vars.password,
          first_name: vars.firstName,
          last_name: vars.lastName,
        },
      });
      return data ?? null;
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      await forgotPassword({ body: { email: vars.email } });
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (vars: { accessToken: string; newPassword: string }) => {
      await resetPassword({
        body: { access_token: vars.accessToken, new_password: vars.newPassword },
      });
    },
  });
}

export function useMeDetail(enabled = true) {
  return useQuery({
    queryKey: queryKeys.meDetail,
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data ?? null;
    },
    enabled,
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: queryKeys.activity,
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string | null; lastName: string | null }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      void queryClient.invalidateQueries({ queryKey: queryKeys.meDetail });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

// ─── Projects ──────────────────────────────────────────────────────────────

export function useProjects(enabled = true, sort: TableSortParams = {}) {
  return useQuery({
    // Unsorted callers (the home view) keep the bare ["projects"] key so their
    // optimistic cache writes and invalidations still match; a sort narrows it.
    queryKey: sort.sortBy ? [...queryKeys.projects, sort] : queryKeys.projects,
    queryFn: async () => {
      const { data } = await listProjects(
        sort.sortBy ? { query: { sort_by: sort.sortBy, sort_dir: sort.sortDir ?? "asc" } } : {},
      );
      return data ?? [];
    },
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useProjectMembers(projectId: number | null, enabled = true) {
  return useQuery({
    queryKey:
      projectId === null ? ["projects", "members", "none"] : queryKeys.projectMembers(projectId),
    queryFn: async () => {
      if (projectId === null) {
        return [];
      }
      const { data } = await listProjectMembers({ path: { project_id: projectId } });
      return data ?? [];
    },
    enabled: enabled && projectId !== null,
  });
}

export function useProjectResourceStatus(projectId: number | null) {
  return useQuery({
    queryKey:
      projectId === null
        ? ["projects", "resources", "none"]
        : queryKeys.projectResourceStatus(projectId),
    queryFn: async () => {
      if (projectId === null) {
        return null;
      }
      const { data } = await getProjectResourcesStatus({ path: { project_id: projectId } });
      return (data ?? null) as ResourceStatusMap | null;
    },
    enabled: projectId !== null,
  });
}

export function useAddProjectMember(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      await addProjectMember({
        path: { project_id: projectId },
        body: { email: vars.email },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectMembers(projectId) });
    },
  });
}

export function useRemoveProjectMember(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { memberUserId: string }) => {
      await removeProjectMember({
        path: { project_id: projectId, member_user_id: vars.memberUserId },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectMembers(projectId) });
    },
  });
}

/**
 * Rotates a project's Supabase Postgres password and updates the repo's CI
 * secret (BOIE-134). Invalidate resource status (the row may reflect the
 * rotation) and the projects list, mirroring other resource-mutating hooks.
 */
export function useResetProjectDbPassword(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await resetProjectDbPassword({ path: { project_id: projectId } });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectResourceStatus(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

// ─── Clients ────────────────────────────────────────────────────────────────

export function useClients() {
  return useQuery({
    queryKey: queryKeys.clients,
    queryFn: async () => {
      const { data } = await listClients();
      return data ?? [];
    },
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string }) => {
      const { data } = await createClient({ body: { name: vars.name } });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients });
    },
  });
}

export function useClient(clientId: number | null) {
  return useQuery({
    queryKey: queryKeys.client(clientId),
    queryFn: async () => {
      if (clientId === null) {
        return null;
      }
      const { data } = await getClient({ path: { client_id: clientId } });
      return data ?? null;
    },
    enabled: clientId !== null,
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { clientId: number } & UpdateClientRequest) => {
      const { clientId, ...body } = vars;
      const { data } = await updateClient({ path: { client_id: clientId }, body });
      return data ?? null;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients });
      void queryClient.invalidateQueries({ queryKey: queryKeys.client(vars.clientId) });
    },
  });
}

/**
 * AI-grounds a client's brand from their website (BOIE-148) — colors, logo,
 * and a design description. Read-only: doesn't touch server state, so there's
 * nothing to invalidate. The caller feeds the result into the edit form and
 * `useUpdateClient` persists whatever the user accepts.
 */
export function useResearchClientBrand() {
  return useMutation({
    mutationFn: async (vars: { url: string }) => {
      const { data } = await researchClientBrand({ body: { url: vars.url } });
      return data ?? null;
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (clientId: number) => {
      await deleteClient({ path: { client_id: clientId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

export function useAdminSetProjectClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { projectId: number; clientId: number | null }) => {
      await adminSetProjectClient({
        path: { project_id: vars.projectId },
        body: { client_id: vars.clientId },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
      // Reassignment changes each client's project_count.
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients });
    },
  });
}

/**
 * Start provisioning a project's production environment (admin only).
 *
 * Resolves as soon as the backend queues the work; provisioning then runs for
 * minutes, so callers watch the project's `prod_status` for the outcome.
 */
export function useAdminPromoteProjectToProd() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { projectId: number }) => {
      const { data } = await adminPromoteProjectToProd({
        path: { project_id: vars.projectId },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Bring a project's Railway services to the template's runtime contract
 * (admin only, BOIE-271 / BOIE-258). Resolves with what changed per
 * environment: variable names, never values. Nothing cached changes.
 */
export function useAdminResyncProjectRailway() {
  return useMutation({
    mutationFn: async (vars: { projectId: number }) => {
      const { data } = await adminResyncProjectRailway({
        path: { project_id: vars.projectId },
      });
      return data;
    },
  });
}

// ─── GitHub / platform access ──────────────────────────────────────────────

export function useGithubStatus() {
  return useQuery({
    queryKey: queryKeys.githubStatus,
    queryFn: async () => {
      const { data } = await githubStatus();
      return data ?? null;
    },
    // The backend live-checks org membership on every call, so a freshly
    // granted membership should surface without a manual reload. Refetch on
    // focus, and while connected-but-not-yet-verified poll every 30s; stop
    // once org access is confirmed (or the account is disconnected).
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const status = query.state.data;
      // No cached status yet (e.g. the initial fetch failed) — keep polling
      // so the banner can recover without waiting for a focus event.
      if (!status) {
        return 30_000;
      }
      return status.connected && status.has_org_access !== true ? 30_000 : false;
    },
  });
}

export function useDisconnectGithub() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await githubDisconnect();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubStatus });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessStatus });
    },
  });
}

export function useGithubConnectUrl() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await githubConnect();
      return data ?? null;
    },
  });
}

export function useAccessStatus() {
  return useQuery({
    queryKey: queryKeys.accessStatus,
    queryFn: async () => {
      const { data } = await getAccessStatus();
      return data ?? null;
    },
    // The backend live-checks Supabase/Railway membership on every call, so
    // freshly granted access should surface without a manual reload. Refetch
    // on focus, and poll every 30s while either platform is not yet confirmed
    // (denied or unverified); stop once both report access.
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const status = query.state.data;
      const bothGranted =
        status?.supabase.has_access === true && status?.railway.has_access === true;
      return bothGranted ? false : 30_000;
    },
  });
}

// ─── User search / invite ──────────────────────────────────────────────────

export function useUserSearch(query: string, exclude: string[]) {
  const trimmed = query.trim();
  const excludeStr = exclude.join(",");
  return useQuery({
    queryKey: queryKeys.userSearch(trimmed, excludeStr),
    queryFn: async () => {
      const { data } = await searchUsers({
        query: { q: trimmed, ...(excludeStr ? { exclude: excludeStr } : {}) },
      });
      return data ?? [];
    },
    enabled: trimmed.length >= 2,
    placeholderData: (prev) => prev,
  });
}

export function useInviteColleague() {
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await inviteUser({ body: { email: vars.email } });
      return data ?? null;
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers(sort: TableSortParams = {}) {
  return useQuery({
    // A sort narrows the key; unsorted keeps the bare ["admin","users"] key so
    // mutation invalidations by queryKeys.adminUsers still match.
    queryKey: sort.sortBy ? [...queryKeys.adminUsers, sort] : queryKeys.adminUsers,
    queryFn: async () => {
      const { data } = await adminListUsers(
        sort.sortBy ? { query: { sort_by: sort.sortBy, sort_dir: sort.sortDir ?? "asc" } } : {},
      );
      return data ?? [];
    },
    placeholderData: (prev) => prev,
  });
}

export function useAdminUser(userId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.adminUser(userId),
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data ?? null;
    },
    enabled: enabled && !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName: string | null;
      lastName: string | null;
      role: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: { first_name: vars.firstName, last_name: vars.lastName, role: vars.role },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUser(vars.userId) });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      const { data } = await adminResetUserPassword({ path: { user_id: vars.userId } });
      return data ?? null;
    },
  });
}

/** Ends every browser session and MCP token of a user (BOIE-246). */
export function useRevokeUserSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      const { data } = await adminRevokeUserSessions({ path: { user_id: vars.userId } });
      return data ?? null;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUser(vars.userId) });
      // The log entry is on the acting admin; refresh the activity feed.
      void queryClient.invalidateQueries({ queryKey: ["admin", "activity"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
    },
  });
}

/**
 * Archive a project (admin only).
 *
 * Dumps the project's data to JSON, then deletes its Supabase project and its
 * Railway project. The GitHub repo and the ScaffoldUI record both survive, so
 * the project stays listed here and its dump stays downloadable.
 *
 * The backend refuses with a 409 when the project holds Supabase Storage
 * files, which a JSON dump cannot capture. `force` is the admin's explicit
 * opt-in to archive anyway and lose them.
 */
export function useAdminArchiveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { projectId: number; force?: boolean }) => {
      const { data } = await adminArchiveProject({
        path: { project_id: vars.projectId },
        query: { force: vars.force ?? false },
      });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

export function useDeleteAdminProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { projectId: number }) => {
      await adminDeleteProject({ path: { project_id: vars.projectId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Re-checks every project's GitHub repo and returns the out-of-sync ones
 * (repo deleted/renamed). Invalidate projects afterwards so any statuses the
 * refresh corrected show up.
 */
export function useRefreshProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await adminRefreshProjects({ query: { background: false } });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Asks every project's own database for its newest sign-in and stores the
 * result on the project (`last_login_at`, `last_login_checked_at`). Slow and
 * network-bound, so it is a deliberate action rather than part of the list
 * query. Invalidate projects afterwards to pick up the refreshed values.
 */
export function useAdminRefreshProjectLastLogins() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await adminRefreshProjectLastLogins();
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Checks scaffolded repos' GitHub setup (BOIE-267): delete branch on merge,
 * Dependabot alerts and fixes, the `supabase` environment locked to `main`,
 * no repo-level SUPABASE_* duplicates, and the Codex reviewer key.
 *
 * `apply: false` (a check) writes nothing to GitHub and covers repos in slug
 * order from `after`, as far as its time budget reaches (`next_after` goes
 * on). `apply: true` fixes what it can on exactly the `repos` given, the ones
 * a check showed. Both record what they saw of each repo's Codex reviewer on
 * its project (`pr_reviewer_configured`), which the project's resource status
 * shows, so each reached project's cached status is invalidated. The projects
 * list does not show that flag and is left alone.
 */
export function useAdminBackfillRepoSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { apply: boolean; repos?: string[]; after?: string | null }) => {
      const { data } = await adminBackfillRepoSetup({
        body: { apply: vars.apply, repos: vars.repos ?? null, after: vars.after ?? null },
      });
      return data ?? null;
    },
    onSuccess: (data) => {
      for (const repo of data?.repos ?? []) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.projectResourceStatus(repo.project_id),
        });
      }
    },
  });
}

// ─── Admin activity ────────────────────────────────────────────────────────

/**
 * Server-side filtered/sorted/paginated activity feed. Every control change
 * re-runs this query with new params; `placeholderData` keeps the previous
 * page on screen while the next one loads so the table doesn't flash empty.
 */
export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: queryKeys.adminActivity(params),
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data ?? null;
    },
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: queryKeys.adminActivityAnalytics(days),
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data ?? null;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: queryKeys.adminActivityActions,
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}
